import { useEffect, useState } from 'react';
import { X, Lock, Server } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { ProxyNode, Provider, Seat } from '@lib/streamFlowApi';

interface CheckoutModalProps {
  provider: Provider;
  memberId: string | null;
  onClose: () => void;
  onCheckout: (seat: Seat) => void;
}

/**
 * 24-hour exclusive seat checkout.
 *
 * As of 2026-05-17, provider-egress pinning makes per-seat exit-node
 * choice meaningless — every seat on a given provider routes through
 * `provider.bound_proxy_node_id` to keep the upstream IPTV credentials
 * tied to a single egress IP. This modal now just informs the user
 * which proxy node their stream will egress through (read from the
 * provider's pin) and reserves the seat for 24 hours so no one else
 * can claim it during the window.
 */
export default function CheckoutModal({ provider, memberId, onClose, onCheckout }: CheckoutModalProps) {
  const [boundProxy, setBoundProxy] = useState<ProxyNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const boundId = (provider as any).bound_proxy_node_id as string | null | undefined;
    if (!boundId) {
      setLoading(false);
      return;
    }
    streamFlowApi.listProxyNodes().then(res => {
      if (res.success) {
        const node = (res.data || []).find(n => n.id === boundId);
        setBoundProxy(node ?? null);
      }
      setLoading(false);
    });
  }, [provider]);

  const handleConfirm = async () => {
    if (!memberId) {
      toast.error('Select a family member first');
      return;
    }
    setSubmitting(true);
    // proxy_node_id intentionally omitted — egress is determined by
    // the provider's bound_proxy_node_id on the server. Passing one
    // would be ignored.
    const res = await streamFlowApi.checkoutSeat({
      provider_id: provider.id,
      member_id: memberId,
    });
    setSubmitting(false);
    if (res.success && res.data) {
      toast.success('Checked out for 24 hours');
      onCheckout(res.data);
      onClose();
    } else {
      toast.error(res.error?.message || 'Checkout failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Checkout — 24h exclusive
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Reserves a seat on <span className="font-medium">{provider.name}</span> for 24 hours.
              No one else can claim it during the window.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
              <Server className="w-4 h-4" />
              Stream egress
            </p>
            <p className="text-xs text-gray-500 mb-3">
              All viewers of this provider egress through the same proxy node — the upstream IPTV provider
              would ban us if its credentials appeared from multiple IPs simultaneously. The admin pins
              the egress when the provider is added.
            </p>

            {loading ? (
              <p className="text-sm text-gray-500 py-4 text-center">Loading proxy info…</p>
            ) : boundProxy ? (
              <div className="border rounded-lg p-3 bg-slate-50 border-slate-200">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    boundProxy.health_status === 'healthy' ? 'bg-green-500' :
                    boundProxy.health_status === 'unhealthy' ? 'bg-red-500' : 'bg-gray-300'
                  }`} />
                  <span className="text-sm font-medium">{boundProxy.name}</span>
                </div>
                <p className="text-[11px] text-gray-500 font-mono mt-0.5 break-all">{boundProxy.host}</p>
                {boundProxy.is_tailnet_only && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    ⚠ Tailnet-only — your viewing device needs Tailscale (or a native IPTV app that uses OS DNS).
                  </p>
                )}
              </div>
            ) : (
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-900">
                  This provider isn't pinned to a proxy node. Streams will egress from the Azure VM (public fallback).
                  Ask an admin to bind it to a home proxy node for tailnet egress.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-white text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !memberId}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 text-sm disabled:opacity-50 flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            {submitting ? 'Checking out…' : 'Checkout 24h'}
          </button>
        </div>
      </div>
    </div>
  );
}
